import { gql, useQuery } from '@apollo/client';
import { constantToLabel, ucWords } from '@ecosconnect/core/src/helpers/string';
import React, { useMemo, useState } from 'react';
import { Flex } from 'rebass';
import DataTable from '../Table/DataTable';
import { Select } from '../Form';
import papaparse from 'papaparse';
import { downloadFile } from '../../helpers/download';
import Button from '../Button/Button';
import { PortalWithState } from 'react-portal';
import AnnualReportDrilldownModal from './AnnualReportDrilldownModal';
import useScrollbarSize from '../../hooks/useScrollbarSize';

const ANNUAL_REPORT_QUERY = gql`
  query AnnualReport(
    $active: JSON
    $tested: JSON
    $failed: JSON
    $failedThenPassed: JSON
    $failedNeverPassed: JSON
    $removed: JSON
    $replaced: JSON
    $new: JSON
  ) {
    active: backflowAssemblies(
      where: $active
      aggregate: [{ col: "id", type: DISTINCT, as: "backflowAssemblies" }]
      groupBy: ["type"]
      limit: null
    ) {
      type
      agg
    }

    tested: backflowTests(
      aggregate: [
        { col: "backflowAssemblyId", type: DISTINCT, as: "backflowAssemblies" }
      ]
      groupBy: ["backflowAssembly.type"]
      where: $tested
      limit: null
    ) {
      backflowAssembly {
        type
      }
      agg
    }

    failed: backflowTests(
      aggregate: [
        { col: "backflowAssemblyId", type: DISTINCT, as: "backflowAssemblies" }
      ]
      groupBy: ["backflowAssembly.type"]
      where: $failed
      limit: null
    ) {
      backflowAssembly {
        type
      }
      agg
    }

    failedThenPassed: backflowTests(
      aggregate: [
        { col: "backflowAssemblyId", type: DISTINCT, as: "backflowAssemblies" }
      ]
      groupBy: ["backflowAssembly.type"]
      where: $failedThenPassed
      limit: null
    ) {
      backflowAssembly {
        type
      }
      agg
    }

    failedNeverPassed: backflowTests(
      aggregate: [
        { col: "backflowAssemblyId", type: DISTINCT, as: "backflowAssemblies" }
      ]
      groupBy: ["backflowAssembly.type"]
      where: $failedNeverPassed
      limit: null
    ) {
      backflowAssembly {
        type
      }
      agg
    }

    removed: backflowTests(
      aggregate: [
        { col: "backflowAssemblyId", type: DISTINCT, as: "backflowAssemblies" }
      ]
      groupBy: ["backflowAssembly.type"]
      where: $removed
      limit: null
    ) {
      job(required: true) {
        id
      }
      backflowAssembly {
        type
      }
      agg
    }

    replaced: backflowTests(
      aggregate: [
        { col: "backflowAssemblyId", type: DISTINCT, as: "backflowAssemblies" }
      ]
      groupBy: ["backflowAssembly.type"]
      where: $replaced
      limit: null
    ) {
      job(required: true) {
        id
      }
      backflowAssembly {
        type
      }
      agg
    }

    new: backflowAssemblies(
      aggregate: [{ col: "id", type: DISTINCT, as: "backflowAssemblies" }]
      groupBy: ["type"]
      where: $new
      limit: null
    ) {
      id
      type
      agg
    }
  }
`;

type AnnualReportEntry = {
  type?: string;
  backflowAssembly?: {
    type?: string;
  };
  agg: {
    backflowAssemblies: number;
  };
};

type AnnualReportKey =
  | 'active'
  | 'tested'
  | 'failed'
  | 'failedThenPassed'
  | 'failedNeverPassed'
  | 'removed'
  | 'replaced'
  | 'new';

type AnnualReportData = Record<AnnualReportKey, AnnualReportEntry[]>;

type AnnualReportWidgetProps = {
  width: number;
  height: number;
};

const AnnualReportWidget = ({
  width,
  height,
}: AnnualReportWidgetProps): JSX.Element => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const startDate = `${year}-01-01 00:00:00`;
  const endDate = `${year}-12-31 23:59:59`;
  const dateRange = { between: [startDate, endDate] };
  const yearOptions: number[] = [];
  let curYear = thisYear - 4;
  while (curYear <= thisYear) {
    yearOptions.push(curYear);
    curYear++;
  }
  const scrollBarSize = useScrollbarSize();

  const wheres: Record<AnnualReportKey, Record<string, any>> = useMemo(
    () => ({
      active: {
        created: {
          lte: Math.ceil(Date.parse(endDate) / 1000),
        },
        or: [
          {
            status: 1,
          },
          {
            updated: {
              gt: Math.ceil(Date.parse(endDate) / 1000),
            },
            replacedByAssemblyId: null,
          },
        ],
      },
      tested: {
        dateTested: dateRange,
        waterSupplierStatus: [1, 2, 3],
        backflowAssemblyId: { not: null },
      },
      failed: {
        dateTested: dateRange,
        waterSupplierStatus: [1, 2, 3],
        backflowAssemblyId: { not: null },
        or: { initialResult: 'FAIL', finalResult: 'FAIL' },
      },
      failedThenPassed: {
        dateTested: dateRange,
        waterSupplierStatus: [1, 2, 3],
        backflowAssemblyId: { not: null },
        initialResult: 'FAIL',
        failedThenPassed: true,
      },
      failedNeverPassed: {
        dateTested: dateRange,
        waterSupplierStatus: [1, 2, 3],
        backflowAssemblyId: { not: null },
        initialResult: 'FAIL',
        failedNeverPassed: true,
      },
      removed: {
        dateTested: dateRange,
        waterSupplierStatus: [1, 2, 3],
        backflowAssemblyId: { not: null },
        'job.formDatas.field': 'installation_status',
        'job.formDatas.value': '3',
      },
      replaced: {
        dateTested: dateRange,
        waterSupplierStatus: [1, 2, 3],
        backflowAssemblyId: { not: null },
        'job.formDatas.field': 'installation_status',
        'job.formDatas.value': '4',
      },
      new: {
        replacesAssemblyId: null,
        installYear: year,
      },
    }),
    [dateRange, year, endDate]
  );

  const { data: annualReportData } = useQuery<AnnualReportData>(
    ANNUAL_REPORT_QUERY,
    {
      variables: wheres,
      fetchPolicy: 'no-cache',
    }
  );

  const indexed = {} as Record<
    AnnualReportKey,
    Record<string, number> & { key: AnnualReportKey }
  >;

  const rows = useMemo<
    (Record<string, number> & { key: AnnualReportKey })[]
  >(() => {
    if (!annualReportData) {
      return [];
    }
    return (Object.keys(annualReportData) as AnnualReportKey[]).map((key) =>
      annualReportData[key].reduce(
        (row, item) => {
          row[(item.type || item.backflowAssembly?.type) as string] =
            item.agg.backflowAssemblies;

          indexed[row.key] = row;
          return row;
        },
        { key } as Record<string, number> & { key: AnnualReportKey }
      )
    );
  }, [annualReportData]);

  // update failedNeverPassed to be failed - failedThenPassed because it's more accurate
  // if (indexed.failedNeverPassed && indexed.failed) {
  //   Object.assign(
  //     indexed.failedNeverPassed,
  //     Object.keys(indexed.failed).reduce(
  //       (row, type) => {
  //         if (typeof indexed.failed[type] !== 'number') {
  //           return row
  //         }
  //         row[type] =
  //           indexed.failed[type] - (indexed.failedThenPassed?.[type] || 0)
  //         return row
  //       },
  //       { key: 'failedNeverPassed' } as Record<string, number> & {
  //         key: AnnualReportKey
  //       }
  //     )
  //   )
  // }

  const max = rows ? Math.max(...rows.map((row) => row.assemblies)) : 0;

  const types =
    rows.length > 0 ? Object.keys(rows[0]).filter((key) => key !== 'key') : [];

  const columns = [
    {
      header: 'Item',
      id: 'key',
      accessor: (row: any) => constantToLabel(row.key),
      width: Math.max(
        width - (rows.length ? types.length * 60 : 0) - 2 - scrollBarSize,
        145
      ),
    },
    ...types.map((type) => {
      return {
        id: type,
        header: ucWords(type),
        accessor: (row: any) => {
          return row[type] || 0;
        },
        width: 60,
      };
    }),
  ];

  const downloadCsv = () => {
    const filename = 'annual-summary-report-' + year + '.csv';
    const csv = papaparse.unparse({
      fields: columns.map((column) => column.header),
      data: rows.map((item) => columns.map((column) => column.accessor(item))),
    });
    downloadFile(filename, csv);
  };

  const [drilldownKey, setDrilldownKey] = useState<AnnualReportKey>();

  return (
    <>
      {rows.length > 0 && width && (
        <Flex flexDirection="column">
          <Flex alignItems="baseline" justifyContent="space-between">
            <Flex alignItems="baseline">
              Year
              <Select
                ml={1}
                mr={1}
                value={year + ''}
                onChange={(e) => {
                  setYear(parseInt(e.target.value, 10));
                }}
              >
                {yearOptions.map((year) => (
                  <option key={year}>{year}</option>
                ))}
              </Select>
            </Flex>
            <Button icon="download" onClick={downloadCsv} />
          </Flex>
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                {portal(
                  <>
                    {drilldownKey && (
                      <AnnualReportDrilldownModal
                        title={constantToLabel(drilldownKey)}
                        where={wheres[drilldownKey]}
                        type={
                          drilldownKey === 'new' || drilldownKey === 'active'
                            ? 'assemblies'
                            : 'tests'
                        }
                        onRowClick={() => {}}
                        onClose={closePortal}
                      />
                    )}
                  </>
                )}
                <DataTable
                  items={rows}
                  onRowClick={(row) => {
                    const key = row.key as AnnualReportKey;
                    setDrilldownKey(key);
                    openPortal();
                  }}
                  columns={columns}
                />
              </>
            )}
          </PortalWithState>
        </Flex>
      )}
    </>
  );
};

export default AnnualReportWidget;
